import { useEffect, createContext, useContext, useState, ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { wsService, WebSocketMessage } from '../services/websocket'
import { useAuthStore } from '../store/auth'

interface WebSocketContextValue {
  isConnected: boolean
  subscribe: (eventType: string, handler: (msg: WebSocketMessage) => void) => () => void
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null)

interface WebSocketProviderProps {
  children: ReactNode
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const { accessToken, isAuthenticated } = useAuthStore()
  const queryClient = useQueryClient()
  const [isConnected, setIsConnected] = useState(false)

  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      wsService.disconnect()
      setIsConnected(false)
      return
    }

    // Connect to WebSocket
    wsService.connect(accessToken)

    // Check connection status periodically
    const statusInterval = setInterval(() => {
      setIsConnected(wsService.isConnected())
    }, 1000)

    // Subscribe to all alert events for cache invalidation
    const unsubscribe = wsService.subscribe('alert.*', (msg: WebSocketMessage) => {
      // Invalidate alerts list
      queryClient.invalidateQueries({ queryKey: ['alerts'] })

      // Invalidate specific alert if we have an ID
      if (msg.alert_id) {
        queryClient.invalidateQueries({ queryKey: ['alerts', msg.alert_id] })
        queryClient.invalidateQueries({ queryKey: ['alerts', msg.alert_id, 'events'] })
        queryClient.invalidateQueries({ queryKey: ['alerts', msg.alert_id, 'notes'] })
      }

      // Show notification for new alerts
      if (msg.type === 'alert.created' && 'Notification' in window) {
        const alertData = msg.data as { title?: string; severity?: string } | undefined
        if (Notification.permission === 'granted' && alertData) {
          new Notification('New Alert', {
            body: alertData.title || 'A new alert has been created',
            icon: '/favicon.ico',
            tag: msg.alert_id,
          })
        }
      }
    })

    return () => {
      clearInterval(statusInterval)
      unsubscribe()
      wsService.disconnect()
      setIsConnected(false)
    }
  }, [isAuthenticated, accessToken, queryClient])

  const value: WebSocketContextValue = {
    isConnected,
    subscribe: wsService.subscribe.bind(wsService),
  }

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>
}

export function useWebSocket(): WebSocketContextValue {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider')
  }
  return context
}
